import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { useSidebar } from "../../layouts/RCAdmin/SidebarContext";
import { UserContext } from "../../contexts";
import { useNotificationSummary } from "../../notifications/useNotificationCenter";
import ThemeSwitch from "./ThemeSwitch";
import "./notifications-bell.scss";
import "../ui/rc-ui.scss";

const IMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IBell = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21a2 2 0 0 1-3.46 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * MobileTopbar — the hamburger + profile + notification row, guaranteed on every
 * mobile screen.
 *
 * Rendered once by `AppShell`, so it covers pages that don't render page chrome of
 * their own — anything on `DefaultLayout` (e.g. the listing preview at
 * `/product/:id`) previously had no way to reach the sidebar on a phone.
 *
 * It is always visible on mobile (sticky via AppShell). Page title bars scroll
 * beneath it; duplicate hamburger / profile / bell in page headers are hidden
 * in CSS — see `.rc-mobile-topbar` and `.rc-ui-topbar__nav-strip` in rc-ui.scss.
 */
const MobileTopbar = () => {
  const history = useHistory();
  const { openMobileNav } = useSidebar();
  // Same defensive read as NotificationsBell — no provider ⇒ inert bell.
  const { user } = useContext(UserContext) ?? {};
  // The popover pattern doesn't fit a phone; the mobile bell goes straight to
  // the full /notifications page. Poll is shared (react-query key) with the
  // desktop bell, so this adds no extra requests.
  const { data: summary } = useNotificationSummary(!!user);
  const unreadCount = summary?.unread_count ?? 0;

  return (
    <div className="rc-mobile-topbar">
      <button
        type="button"
        // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): legacy control. The class drives the mobile-only display and the rc-ui.scss :has() nav-strip rules; Phase 3 migrates it to <IconButton>.
        className="rc-menu-btn rc-mobile-topbar__menu"
        onClick={openMobileNav}
        aria-label="Open menu"
      >
        <IMenu />
      </button>

      <div className="rc-mobile-topbar__right">
        <ThemeSwitch compact size="sm" />
        <button
          type="button"
          // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): legacy control. The class drives the mobile-only display and the rc-ui.scss :has() nav-strip rules; Phase 3 migrates it to <IconButton>.
          className="rc-ui-topbar__icon-btn"
          aria-label="Profile"
          onClick={() => history.push("/profile")}
        >
          <IUser />
        </button>
        <button
          type="button"
          // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): legacy control. The class drives the mobile-only display and the rc-ui.scss :has() nav-strip rules; Phase 3 migrates it to <IconButton>.
          className="rc-ui-topbar__icon-btn rc-bell"
          aria-label={unreadCount ? `Notifications (${unreadCount})` : "Notifications"}
          onClick={() => history.push("/notifications")}
        >
          <IBell />
          {unreadCount > 0 && (
            <span className="rc-bell__badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default MobileTopbar;
